/**
 * predictivePastoralSignals.js
 * Pure function — detects pastoral patterns that warrant attention.
 *
 * Language rules (strictly pastoral, not analytics):
 *   ✓  "requiere atención pastoral"
 *   ✓  "sería bueno retomar contacto"
 *   ✓  "hay señales de distanciamiento"
 *   ✓  "convendría acompañamiento cercano"
 *   ✗  "usuario inactivo"
 *   ✗  "engagement decay"
 *   ✗  "retention failure"
 */

/**
 * @param {{
 *   state:      ReturnType<import("./relationshipStateResolver").resolveRelationshipState>,
 *   trajectory: ReturnType<import("./pastoralTrajectoryAnalyzer").analyzeTrajectory>,
 *   history:    import("./relationalMemoryEngine").RelationalRecord | null,
 *   tone:       string,
 *   entityType: "family" | "person" | "church",
 * }} params
 * @returns {{
 *   signals:  string[],
 *   priority: "high" | "medium" | "low" | "none",
 *   count:    number,
 * }}
 */
export function detectPastoralSignals({ state, trajectory, history, tone, entityType }) {
  const signals = [];
  const { followup_frequency, last_contact_days, pastoral_risk, attendance_signal } = state;
  const signals_hist = history?.signals ?? [];

  // ── High-risk patterns ────────────────────────────────────────────────────

  if (pastoral_risk === "high" && followup_frequency === "absent") {
    const label = entityType === "family" ? "la familia" : "la persona";
    const days  = last_contact_days ?? "muchos";
    signals.push(`Sin visita pastoral en ${days} días — convendría retomar contacto con ${label}.`);
  }

  if (tone === "sensitive") {
    signals.push("Hay situaciones delicadas activas que merecen acompañamiento cercano.");
  }

  // ── Distancing pattern ────────────────────────────────────────────────────

  if (trajectory.stage === "distanciamiento") {
    signals.push("Hay señales de distanciamiento progresivo.");
  }

  if (trajectory.stage === "aislado") {
    signals.push("Situación de aislamiento pastoral — requiere atención prioritaria.");
  }

  // ── Recurrent prayer without followup ────────────────────────────────────

  const recentPrayerSignals = signals_hist.filter((s) => {
    const daysAgo = (Date.now() - s.ts) / 86_400_000;
    return daysAgo <= 30 && (s.tone === "sensitive" || s.prayerCount >= 2);
  }).length;

  if (recentPrayerSignals >= 2) {
    signals.push("Oraciones recurrentes registradas — sería bueno verificar el estado actual.");
  }

  // ── Persistent risk across sessions ──────────────────────────────────────

  const riskySessions = signals_hist.slice(0, 4).filter(
    (s) => s.tone === "followup_urgent" || s.tone === "sensitive"
  ).length;

  if (riskySessions >= 3 && !signals.some((s) => s.includes("aislamiento"))) {
    signals.push("Situación de riesgo pastoral persistente en sesiones anteriores.");
  }

  // ── Restoration momentum (positive) ─────────────────────────────────────

  if (trajectory.stage === "restaurando" && attendance_signal === "returning") {
    signals.push("Hay señales de retorno — buen momento para fortalecer el vínculo pastoral.");
  }

  // ── Baptism process without recent contact ────────────────────────────────

  if (tone === "accompany" && followup_frequency !== "regular") {
    signals.push("Proceso espiritual activo sin seguimiento regular — convendría agendar una visita.");
  }

  // ── Priority ─────────────────────────────────────────────────────────────

  const priority =
    trajectory.stage === "aislado" || pastoral_risk === "high" || riskySessions >= 3 ? "high" :
    signals.length >= 2 ? "medium" :
    signals.length === 1 ? "low" :
    "none";

  return { signals, priority, count: signals.length };
}
